/**
 * The update sequence a shell runs on launch.
 *
 * ```
 *   check ── up_to_date ────────────────────────────────► done
 *     │
 *     └─ available ─ download each file ─ verify ─ stage ─ swap ─► active
 *                        │                │        │       │
 *                        └────────────────┴────────┴───────┴──► failed (running bundle untouched)
 * ```
 *
 * Two rules make this safe on a phone rather than merely correct on a desk:
 *
 *  1. **Nothing the running app depends on is touched until the last step.** Files
 *     are written to a staging area, verified against the manifest there, and only
 *     a complete, verified bundle is promoted. A dropped connection, a truncated
 *     file, a tampered asset and a captive portal all end in `failed`, with the app
 *     that is running still running.
 *  2. **The swap is the store's problem, not this module's.** `activate()` means
 *     "make what I staged the bundle the shell boots next time", and how that is
 *     done atomically - a rename, a pointer file, a native call - belongs to the
 *     platform. This module never writes into the live bundle directory, so it has
 *     no way to corrupt it even by mistake.
 *
 * What it does *not* do is decide that a new bundle is healthy. A bundle can verify
 * perfectly and still crash on launch, which is why the swap is paired with a boot
 * check (`boot.ts`) on the other side: the shell counts boots, and the JS reports
 * one when the app is up.
 */
import { parseManifest, sha256Hex, } from './manifest.js';
/** The URL one file of a bundle is published at, resolved beside the manifest. */
export function bundleFileUrl(manifestUrl, path) {
    return new URL(`files/${path}`, manifestUrl).toString();
}
const messageOf = (error) => (error instanceof Error ? error.message : String(error));
export function createUpdater(options) {
    const { manifestUrl, store } = options;
    const doFetch = options.fetchImpl ?? fetch;
    const hash = options.hash ?? sha256Hex;
    const report = (state) => {
        options.onState?.(state);
        return state;
    };
    const failed = (reason, version = null) => report({ status: 'failed', reason, version });
    async function fetchManifest() {
        report({ status: 'checking' });
        let response;
        try {
            // The manifest is the one thing that must never be stale: it is how the
            // device learns there is a new one. Asked for with `no-cache` and answered
            // with `no-store`, rather than with `fetch`'s `cache` option, which the
            // platform types here do not carry and which a raw HTTP cache would ignore
            // anyway.
            response = await doFetch(manifestUrl, {
                headers: { accept: 'application/json', 'cache-control': 'no-cache' },
            });
        }
        catch (error) {
            return failed(`the update server could not be reached: ${messageOf(error)}`);
        }
        if (!response.ok) {
            return failed(`the update server answered ${response.status} for the manifest`);
        }
        let payload;
        try {
            payload = await response.json();
        }
        catch (error) {
            // A captive portal, a proxy and an HTML error page all arrive here. The
            // device has to say "that was not a manifest" rather than throwing into a
            // launch path where nobody sees it.
            return failed(`the update server answered with something that is not JSON: ${messageOf(error)}`);
        }
        let manifest;
        try {
            manifest = parseManifest(payload, options.kind);
        }
        catch (error) {
            return failed(`the update server answered with something that is not a usable manifest: ${messageOf(error)}`);
        }
        const running = await store.running();
        if (running === manifest.version)
            return report({ status: 'up_to_date', version: manifest.version });
        // The version is not repeated beside the manifest: two fields carrying the
        // same string is two fields that can disagree.
        return report({ status: 'available', manifest });
    }
    async function install(manifest) {
        // Checked again here rather than trusted from the caller: installing the
        // bundle that is running is a no-op that must not touch the disk.
        if ((await store.running()) === manifest.version) {
            return report({ status: 'up_to_date', version: manifest.version });
        }
        try {
            let done = 0;
            for (const file of manifest.files) {
                report({ status: 'downloading', version: manifest.version, done, total: manifest.files.length });
                const url = bundleFileUrl(manifestUrl, file.path);
                // Also `no-cache`: a path is only content-addressed for hashed assets, and
                // `index.html` is the same path in every release.
                const response = await doFetch(url, { headers: { 'cache-control': 'no-cache' } });
                if (!response.ok) {
                    return await abandon(`${file.path}: the bundle server answered ${response.status}`, manifest.version);
                }
                const bytes = new Uint8Array(await response.arrayBuffer());
                if (bytes.byteLength !== file.bytes) {
                    return await abandon(`${file.path}: ${bytes.byteLength} bytes, not ${file.bytes}`, manifest.version);
                }
                const digest = await hash(bytes);
                if (digest !== file.sha256) {
                    return await abandon(`${file.path}: sha256 ${digest}, not ${file.sha256}`, manifest.version);
                }
                await store.stage(manifest.version, file.path, bytes);
                done += 1;
            }
            report({ status: 'staged', version: manifest.version });
            await store.activate(manifest.version);
            return report({ status: 'active', version: manifest.version });
        }
        catch (error) {
            return await abandon(`the update could not be installed: ${messageOf(error)}`, manifest.version);
        }
    }
    /** Every failure leaves the same way: staging discarded, running bundle untouched. */
    async function abandon(reason, version) {
        try {
            await store.discardStaged();
        }
        catch {
            // A staging area that cannot be cleaned up is not a reason to hide why the
            // update failed; the next launch discards it again.
        }
        return failed(reason, version);
    }
    return {
        check: fetchManifest,
        install,
        async update() {
            const checked = await fetchManifest();
            if (checked.status !== 'available')
                return checked;
            return install(checked.manifest);
        },
    };
}
//# sourceMappingURL=updater.js.map